import type { PreloadedQuery } from 'react-relay';
import {
	// eslint-disable-next-line jira/import/no-restricted-import
	fieldsResource,
} from '@jira/router-resources__business-fields/src/fieldsResource.tsx';
// eslint-disable-next-line jira/import/no-restricted-import
import type { Field } from '@jira/router-resources__business-fields/src/FieldEdge.types.tsx';
import { issueNavigatorRelayResource } from '@jira/router-resources__issue-navigator/src/issueNavigatorRelayResource.tsx';
import type { ProjectContext } from '@jira/router-resources__project-context/src/ProjectContext.types.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import type { main_IssueNavigatorQuery } from '@jira/issue-navigator__issue-navigator/src/__generated__/main_IssueNavigatorQuery.graphql';
/**
 * Extract project key from issue key (e.g., "ABC-123" → "ABC")
 */
const extractProjectKeyFromIssueKey = (issueKey: string): string | null => {
	if (!issueKey) return null;
	const parts = issueKey.split('-');
	return parts.length >= 2 ? parts[0] : null;
};
/**
 * Creates a conditional resource wrapper that only loads when hasList query param is true.
 * This allows SSR to conditionally load list view resources without impacting default work item performance.
 *
 * For work-item routes, this also extracts projectKey from the issueKey param to ensure proper JQL scoping.
 */
function createConditionalListResource<T>(
	baseResource: {
		getData: (
			routerContext: RouterDataContext,
			resourceContext: ResourceStoreContext,
		) => Promise<T> | T;
		maxAge?: number;
	},
	resourceName: string,
) {
	return createResource<T | null>({
		type: `work-item-list-${resourceName}`,
		getKey: () => `work-item-list-${resourceName}`,
		getData: async (routerContext: RouterDataContext, resourceContext: ResourceStoreContext) => {
			// Only load if hasList query param is explicitly set to 'true'
			if (routerContext.query?.hasList === 'true') {
				// Extract projectKey from issueKey for work-item routes
				const issueKey = routerContext.match.params.issueKey;
				const projectKey = issueKey ? extractProjectKeyFromIssueKey(issueKey) : null;
				// Create a modified context with projectKey for resources that need it
				const modifiedContext: RouterDataContext = {
					...routerContext,
					match: {
						...routerContext.match,
						params: {
							...routerContext.match.params,
							...(projectKey && { projectKey }),
						},
					},
				};
				return baseResource.getData(modifiedContext, resourceContext);
			}
			// Return null to skip loading when not needed
			return null;
		},
		maxAge: baseResource.maxAge || 0,
	});
}
/**
 * issue navigator relay resource.
 */
const conditionalIssueNavigatorResource = createConditionalListResource(
	issueNavigatorRelayResource,
	'issue-navigator',
);
/**
 * fields resource.
 */
const conditionalFieldsResource = createConditionalListResource(fieldsResource, 'fields');
/**
 * project context resource.
 */
const conditionalProjectContextResource = createConditionalListResource(
	projectContextResource,
	'project-context',
);
export const workItemConditionalListResources: {
	conditionalProjectContextResource: RouteResource<ProjectContext | null>;
	conditionalFieldsResource: RouteResource<Field[] | null>;
	conditionalIssueNavigatorResource: RouteResource<PreloadedQuery<
		main_IssueNavigatorQuery,
		Record<string, unknown>
	> | null>;
} = {
	conditionalProjectContextResource,
	conditionalFieldsResource,
	conditionalIssueNavigatorResource,
};
